import { useState, useEffect, useRef } from 'react'
import { canUseDOM } from '../lib/utils'

/**
 * Tracks the window dimensions with configurable debounce.
 * SSR safe — returns 0 for both dimensions on the server.
 *
 * @param {number} [debounceMs=100] - Debounce delay in milliseconds.
 * @returns {{ width: number, height: number }} Current viewport dimensions (excluding scrollbars).
 */
export default function useWindowSize(debounceMs = 100) {
  const [size, setSize] = useState(() => ({
    width: canUseDOM ? window.document.documentElement.clientWidth : 0,
    height: canUseDOM ? window.document.documentElement.clientHeight : 0,
  }))
  const timeoutRef = useRef(null)

  useEffect(() => {
    const handleResize = () => {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => {
        setSize({
          width: window.document.documentElement.clientWidth,
          height: window.document.documentElement.clientHeight,
        })
      }, debounceMs)
    }
    window.addEventListener('resize', handleResize)
    return () => {
      clearTimeout(timeoutRef.current)
      window.removeEventListener('resize', handleResize)
    }
  }, [debounceMs])

  return { ...size }
}
